import { Suspense } from "react";
import { ErrorBoundary } from "./ErrorBoundary";
import { useSearch } from "wouter";
import { webTools } from "../../tools/web";
import { useCompany } from "../lib/company";

/*
 * Every tool's record windows that the address has open, over whatever page is showing: like
 * ServiceM8's job card, a record opens where you are (Home's search, a list, another tool) and
 * stays open as you move between pages. Each tool's windows load only once one of them is open.
 */
/** Every tool's window keys: what a link to another page carries along, so open windows stay open. */
const WINDOW_PARAMS = new Set(Object.values(webTools).flatMap((t) => t.windowParams ?? []));

/** `href` (which may have its own query and #) with the address's open windows added, for links between pages. */
export function useWithWindows() {
    return withWindowsOf(useSearch());
}

/**
 * `search`'s open windows added to an href: each window key the href doesn't set itself (one that does, e.g. a link that
 * opens a window, says which), so carrying them again and again never repeats them.
 */
export function withWindowsOf(search: string) {
    const open = [...new URLSearchParams(search)].filter(([key]) => WINDOW_PARAMS.has(key));
    return (href: string) => {
        if (!open.length) return href;
        const [path = "", hash] = href.split("#");
        const [pathname = "", query = ""] = path.split("?");
        const params = new URLSearchParams(query);
        for (const [key, value] of open) if (!params.has(key)) params.append(key, value);
        const anchor = hash === undefined ? "" : "#" + hash;
        return `${pathname}?${params.toString()}${anchor}`;
    };
}

/** An address's query without its windows: what page it is. */
export function pageQuery(search: string) {
    const params = new URLSearchParams([...new URLSearchParams(search)].filter(([key]) => !WINDOW_PARAMS.has(key)));
    return params.size ? "?" + params.toString() : "";
}

export function ToolWindows() {
    const company = useCompany();
    const params = new URLSearchParams(useSearch());
    return company.tools.map((id) => {
        const tool = webTools[id];
        if (!tool?.Windows || !tool.windowParams?.some((key) => params.has(key))) return null;
        return (
            <ErrorBoundary key={id}>
                <Suspense fallback={null}>
                    <tool.Windows />
                </Suspense>
            </ErrorBoundary>
        );
    });
}
